
import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, Building2 } from 'lucide-react';
import { api } from '../../services/api';
import { Client } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  type: 'auto_other' | 'recurring';
  data: any;
  onSave: (data: any) => Promise<void>;
}

const RuleModal: React.FC<Props> = ({ isOpen, onClose, type, data, onSave }) => {
  const [formData, setFormData] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);

  useEffect(() => {
    if (isOpen) {
      api.clients.getAll().then(setClients);
      if (data) setFormData({ ...data });
      else setFormData({});
    }
  }, [isOpen, data]);

  const matchedClient = useMemo(() => {
    if (!formData.client_code) return null;
    return clients.find(c => c.client_code === formData.client_code);
  }, [formData.client_code, clients]);

  useEffect(() => {
    if (matchedClient && !formData.client_name) {
      setFormData(prev => ({
        ...prev,
        client_name: matchedClient.client_name
      }));
    }
  }, [matchedClient]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
        await onSave(formData);
        onClose();
    } finally {
        setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md relative shadow-2xl animate-in zoom-in duration-200 max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24}/></button>
        <h2 className="text-xl font-bold mb-6 text-gray-800">
            {type === 'auto_other' ? 'その他自動振り分け設定' : '毎月振込設定'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">得意先CD</label>
                <input 
                    type="text" 
                    value={formData.client_code||''} 
                    onChange={e => setFormData({...formData, client_code: e.target.value})} 
                    placeholder="例：123456"
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono" 
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">得意先名</label>
                <input 
                    type="text" 
                    value={formData.client_name||''} 
                    onChange={e => setFormData({...formData, client_name: e.target.value})} 
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none" 
                    placeholder="マスタ名称"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">振込依頼人名</label>
                <input 
                    type="text" 
                    value={formData.remitter_name||''} 
                    onChange={e => setFormData({...formData, remitter_name: e.target.value})} 
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none font-bold text-gray-800" 
                    placeholder="例：ｶ) ｳｴﾑﾗｸﾞﾐ"
                    required 
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    設定金額 <span className="text-[10px] font-normal text-gray-400">（任意：未入力＝任意一致）</span>
                </label>
                <input
                    type="number"
                    value={type === 'auto_other' ? (formData.amount ?? '') : (formData.expected_amount ?? '')}
                    onChange={e => {
                        const val = parseInt(e.target.value) || 0;
                        if(type === 'auto_other') setFormData({...formData, amount: val});
                        else setFormData({...formData, expected_amount: val});
                    }}
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none text-right font-mono"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">備考</label>
                <textarea 
                    value={formData.notes||''} 
                    onChange={e => setFormData({...formData, notes: e.target.value})} 
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none h-20 text-sm" 
                    placeholder="管理用メモなど"
                />
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button type="button" onClick={onClose} className="border border-gray-300 px-4 py-2 rounded text-gray-700 hover:bg-gray-50 transition text-sm font-bold">キャンセル</button>
                <button type="submit" disabled={isSaving} className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 font-black transition flex items-center gap-2 shadow-md">
                    <Save size={18}/> 保存
                </button>
            </div>
        </form>
      </div>
    </div>
  );
};
export default RuleModal;
